import { ComponentProps, ComponentType } from 'react';
import styled, { createGlobalStyle, css } from 'styled-components';

import { breakpoints } from '@r1-frontend/entities/Layout/Header/types';

import GridContainer from '@r1-frontend/ui-react/components/layouts/gridContainer';
import WideContainer, { wideBreakpoints } from '@r1-frontend/ui-react/components/layouts/wideContainer';
import { COLORS } from '@r1-frontend/ui-react/tokens/colors';
import { FONTS } from '@r1-frontend/ui-react/tokens/fonts';

export const TOP_WRAPPER_HEIGHT = 36;
export const MAIN_WRAPPER_HEIGHT = 64;

const commonWrapperWithIndentStyle = css`
    width: inherit;
    background-color: ${COLORS.BgMain};
    max-width: 100%;
    min-width: 288px;
    overflow: hidden;

    > :first-child {
        width: inherit;
        min-width: inherit;
        height: inherit;
        background-color: inherit;
        flex-wrap: nowrap;
        position: fixed;
    }
`;

export const MobileMainWrapper = styled.div`
    display: flex;
    flex-wrap: nowrap;
    align-items: center;
`;

export const MobileContainer: ComponentType<ComponentProps<typeof WideContainer>> = styled(WideContainer)`
    display: flex;
    flex-wrap: nowrap;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
`;

export const MobileMainWrapperOffset = styled.div`
    ${commonWrapperWithIndentStyle};

    height: ${MAIN_WRAPPER_HEIGHT}px;
`;

export const MobileTopWrapperOffset = styled.div<{ isScrollingUp: boolean }>`
    ${commonWrapperWithIndentStyle};

    height: ${p => p.isScrollingUp ? `${TOP_WRAPPER_HEIGHT}px` : '0'};
    transition: height .25s;
`;

export const MobileTopWrapper = styled.div`
    display: flex;
    flex-wrap: nowrap;
    align-items: center;

    @media (max-width: ${breakpoints.mMobile}) {
        &::after {
            content: '';
            position: absolute;
            bottom: 0;
            left: 16px;
            right: 16px;
            height: 2px;
            box-shadow: inset 0 -2px ${COLORS.Outline_Light};
        }
    }
`;

export const MobileHeaderWrapper: ComponentType<ComponentProps<typeof GridContainer>> = styled(GridContainer)<{ withShadow: boolean }>`
    display: none;

    @media (max-width: ${wideBreakpoints.laptop}) {
        display: block;
        min-width: 288px;
        width: inherit;
        position: relative;
        padding-right: 0;
        padding-left: 0;
        margin-right: 0;
        margin-left: 0;
        background-color: ${COLORS.BgMain};
        box-shadow: ${p => p.withShadow ? '0 8px 20px -2px rgb(41 44 48 / 10%)' : 'none'};
        z-index: 11;
    }
`;

export const DropDownContainerTop = styled.div`
    height: auto;
    width: 100%;
    padding-left: 24px;
    padding-right: 24px;
`;

export const DropDownContainerBottom = styled.div`
    margin: 24px 0 12px;
    ${FONTS.XS};
    flex-direction: row;
    display: flex;
    justify-content: space-between;
    flex-wrap: nowrap;
    padding-left: 24px;
    padding-right: 24px;
`;

export const LinkItemDrop = styled.a`
    ${FONTS.MediumS};
    color: ${COLORS.TextPrimary};
`;

export const MobileTopMenuItemBlock = styled.div`
    flex-direction: column;
`;

export const IconTextBlock = styled.div`
    ${FONTS.MediumXXS};
    background-color: ${COLORS.BgMain};
    text-align: center;
    color: ${COLORS.TextHint};
    height: auto;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    flex-wrap: wrap;
`;

export const BurgerBlock = styled.div`
    display: flex;
    flex-wrap: nowrap;
    justify-content: space-between;
    align-items: center;
    border-bottom: 2px solid ${COLORS.BgSecondary};
    padding: 20px 24px 12px;
    height: 71px;

    @media (max-width: ${breakpoints.mMobile}) {
        border-bottom: none;
        height: auto;
        padding: 12px 24px;
    }
`;

export const BurgerWrapper = styled.div`
    flex-wrap: nowrap;
    justify-content: space-between;
    align-items: center;
    width: 100%;

    @media (max-width: ${breakpoints.mMobile}) {
        border-bottom: 2px solid ${COLORS.BgSecondary};
        padding-bottom: 8px;
    }
`;

export const BurgerIcon = styled.div<{ isBurgerOpen?: boolean }>`
    padding: 8px;
    cursor: pointer;
`;

export const MobileTopMenuItem = styled.div`
    ${FONTS.Medium};
    color: ${COLORS.GRAY_DARK};
    padding: 6px 8px;
`;

export const MobilePartnersItem = styled.div`
    flex-direction: column;
    max-width: 112px;
    width: 100%;
`;

export const Right = styled.div`
    display: flex;
    flex-wrap: nowrap;
    align-items: center;
    ${FONTS.XS};
    gap: 8px;
`;

export const GlobalStyle = createGlobalStyle<{ isBlockedBody: boolean }>`
    ${p => p.isBlockedBody && css`
        body {
            position: fixed;
            overflow-y: scroll;
            width: 100%;
        }
    `}
`;

export const BurgerMenu = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    height: 100vh;
    width: 100%;
    background: rgba(32, 32, 32, .6);
    overflow: hidden;
    overflow-y: auto;
    z-index: 200;
`;

export const BurgerMenuWrapper: ComponentType<ComponentProps<typeof GridContainer>> = styled(GridContainer)`
    max-width: 408px;
    min-width: auto;
    padding: 0;
    width: 100%;
    position: relative;
    overflow-x: hidden;
    flex-direction: column;
    overflow-y: auto;
    margin-right: 0;
    background: ${COLORS.BgMain};
    background-color: ${COLORS.BgMain};
`;

export const PromoImg = styled.div`
    background-color: ${COLORS.BgSurface};
    width: 40px;
    height: 40px;
    padding: 12px;
    border-radius: 50px;
    margin-bottom: 4px;
    cursor: pointer;
    justify-content: center;
    display: flex;

    img {
        width: 100%;
        height: 100%;
    }
`;

export const RightMenu = styled.div`
    margin-left: auto;
    position: relative;
    ${FONTS.XXS};
`;

export const SmallMobileWrapper = styled.div`
    display: none;

    @media (max-width: ${breakpoints.mMobile}) {
        display: flex;
        width: 100%;
    }
`;

export const MobileWrapper = styled.div`
    display: flex;
    width: 100%;

    @media (max-width: ${breakpoints.mMobile}) {
        display: none;
    }
`;

export const ServiceAndPhoneBlock = styled.div`
    margin-left: auto;
`;

export const ServiceBlock = styled.ul`
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    align-items: center;
`;
